// File Metadata:
// Filename: axiosInstance.js
// Version: 1.01
// Created: 2025-05-03 01:20:56
// Last Modified: 2025-05-20
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: Enhanced axios instance with improved JWT token expiration handling

//// filepath: g:\Development\Projects\familysync\frontend\src\store\api\axiosInstance.js
import axios from 'axios';
import { store } from '../index'; // Our Redux store
import { refreshToken, logout } from '../slices/userSlice';
import { handleAuthError } from '../../utils/errorHandler';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL ? `${import.meta.env.VITE_APP_API_URL}/api` : '/api',
  withCredentials: true   // Important: allow sending/receiving cookies
});

// 1) Request Interceptor: Add Authorization header if we have an access token in localStorage
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token'); // Access token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 2) Response Interceptor: If we get a 401, automatically attempt to refresh then retry
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    console.log('Axios interceptor caught error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      hasBeenRetried: originalRequest._retry
    });
    
    // If request was 401 (Unauthorized) and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('Attempting token refresh for 401 error...');
      originalRequest._retry = true; // Mark request as retried
      
      try {
        // Use the centralized error handler
        console.log('Calling handleAuthError...');
        const shouldRetry = await handleAuthError(error);
        
        console.log('handleAuthError returned:', shouldRetry);
        
        if (shouldRetry) {
          // After refresh, read the updated token from localStorage
          const newToken = localStorage.getItem('token');
          console.log('Got new token from localStorage:', !!newToken);
          if (newToken) {
            console.log('Retrying original request with new token');
            // Update the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            // Retry the original request
            return axiosInstance(originalRequest);
          }
        }
      } catch (handlerError) {
        console.error('Error in auth error handler:', handlerError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;